'use client';
import React, { useState } from 'react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockPerks, perkCategories, Perk } from '@/data/mockPerks';
import { Gift, ArrowRight, Info, Handshake } from 'lucide-react';
import Link from 'next/link';

const categoryFilters = perkCategories.map((c) => ({ label: c, value: c === 'All' ? 'all' : c }));

const categoryColors: Record<string, string> = {
  Nightlife: '#9B7FFF',
  Dining: '#4CC9FF',
  Wellness: '#2DD4A0',
  Services: '#F0A832',
  'Luxury & VIP': '#FF6B9D',
  'Home Services': '#4CC9FF',
};

function PerkSkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-20 rounded-full" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-4/5 mb-4" />
      <Skeleton className="h-8 w-28 rounded-lg" />
    </div>
  );
}

function PerkTypeLabel({ type }: { type: Perk['type'] }) {
  if (type === 'resident-benefit') {
    return (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
        style={{ background: 'rgba(45,212,160,0.10)', color: '#2DD4A0', border: '1px solid rgba(45,212,160,0.20)' }}
      >
        Resident Benefit
      </span>
    );
  }
  if (type === 'sponsor-supported') {
    return (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
        style={{ background: 'rgba(240,168,50,0.10)', color: '#F0A832', border: '1px solid rgba(240,168,50,0.20)' }}
      >
        Sponsored
      </span>
    );
  }
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide"
      style={{ background: 'rgba(167,163,196,0.08)', color: 'rgba(167,163,196,0.50)', border: '1px solid rgba(167,163,196,0.15)' }}
    >
      Coming Soon
    </span>
  );
}

function PerkCard({ perk }: { perk: Perk }) {
  const color = categoryColors[perk.category] || '#4CC9FF';
  const isComingSoon = perk.status === 'coming-soon';

  return (
    <div
      className={`neon-card p-5 h-full flex flex-col ${isComingSoon ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-[14px] leading-snug mb-1.5" style={{ color: '#F2F4FF' }}>
            {perk.partnerName}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[11px] px-2 py-0.5 rounded font-medium"
              style={{ background: `${color}10`, color, border: `1px solid ${color}20` }}
            >
              {perk.category}
            </span>
            <PerkTypeLabel type={perk.type} />
          </div>
        </div>
      </div>

      <p className="text-[13px] font-medium mb-2" style={{ color: '#F2F4FF' }}>
        {perk.perkSummary}
      </p>

      <div
        className="px-3 py-2.5 rounded-lg mb-4 flex-1"
        style={{ background: `${color}06`, border: `1px solid ${color}14` }}
      >
        <p className="text-[12px] leading-relaxed" style={{ color: '#A7A3C4' }}>
          <span className="font-semibold" style={{ color }}>Resident benefit: </span>
          {perk.residentBenefit}
        </p>
      </div>

      {perk.isMockExample && (
        <div className="flex items-start gap-1.5 mb-3">
          <Info size={11} style={{ color: 'rgba(167,163,196,0.35)', marginTop: 1, flexShrink: 0 }} />
          <p className="text-[11px]" style={{ color: 'rgba(167,163,196,0.35)' }}>
            Example perk structure — reserved for a future verified partner.
          </p>
        </div>
      )}

      <button
        disabled={isComingSoon}
        className={`w-full flex items-center justify-center gap-2 text-[13px] font-medium py-2.5 rounded-lg transition-all duration-150 ${
          isComingSoon
            ? 'cursor-not-allowed' :'hover:opacity-80 active:scale-[0.97]'
        }`}
        style={{
          background: isComingSoon ? 'rgba(255,255,255,0.04)' : `${color}14`,
          color: isComingSoon ? 'rgba(167,163,196,0.35)' : color,
          border: `1px solid ${isComingSoon ? 'rgba(255,255,255,0.07)' : `${color}25`}`,
        }}
      >
        {perk.cta}
        {!isComingSoon && <ArrowRight size={12} />}
      </button>
    </div>
  );
}

export default function PerksPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading] = useState(false);

  const filtered = mockPerks.filter((p) =>
    activeCategory === 'all' || p.category === activeCategory
  );

  const activePerks = mockPerks.filter((p) => p.status === 'active').length;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Resident Perks"
          title="Perks & Privileges"
          description="Preferred access, resident benefits, and curated partnerships for Panorama residents. This program is growing — more partners coming soon."
          stat={{ value: String(activePerks), label: 'active perks' }}
        />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          {/* Program intro */}
          <div
            className="px-5 py-4 rounded-xl mb-8"
            style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
          >
            <div className="flex items-start gap-3">
              <Gift size={16} style={{ color: '#4CC9FF', marginTop: 2, flexShrink: 0 }} />
              <div>
                <p className="text-[13px] font-medium mb-1" style={{ color: '#F2F4FF' }}>
                  The Panorama Perks Program
                </p>
                <p className="text-[12px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                  Perks are curated for Panorama residents — local businesses, hosts, and service providers who offer special value or access to our community. Some perks are resident benefits (free services worth knowing about). Others are sponsor-supported partnerships. Future partner slots are clearly marked.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <FilterRow filters={categoryFilters} active={activeCategory} onChange={setActiveCategory} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <PerkSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title="No perks in this category"
              description="Check back soon — we're adding new partners regularly."
              icon={<Gift size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveCategory('all')}>
                  Show all perks
                </button>
              }
            />
          )}

          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((perk) => (
                <PerkCard key={perk.id} perk={perk} />
              ))}
            </div>
          )}

          {/* Partner CTA */}
          <div
            className="mt-14 rounded-xl p-8 lg:p-10"
            style={{ background: 'linear-gradient(135deg, rgba(122,92,255,0.08) 0%, rgba(76,201,255,0.05) 100%)', border: '1px solid rgba(122,92,255,0.18)' }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(122,92,255,0.12)', border: '1px solid rgba(122,92,255,0.22)' }}
              >
                <Handshake size={20} style={{ color: '#9B7FFF' }} />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-semibold text-[18px] mb-2" style={{ color: '#F2F4FF' }}>
                  Want to Partner With the Community?
                </h3>
                <p className="text-[14px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                  We selectively partner with businesses and service providers who offer genuine, meaningful value to Panorama residents. This is not a directory listing or a generic discount program.
                </p>
                <p className="text-[13.5px] leading-relaxed mb-5" style={{ color: 'rgba(167,163,196,0.75)' }}>
                  If you offer something exceptional — exclusive access, a meaningful resident benefit, or a service that genuinely fits the Panorama lifestyle — we'd like to hear from you. We prioritize quality over volume. Generic offers are not a fit.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    href="/request-access"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold transition-all duration-150 hover:opacity-90 active:scale-95"
                    style={{ background: 'rgba(122,92,255,0.15)', color: '#9B7FFF', border: '1px solid rgba(122,92,255,0.25)' }}
                  >
                    Apply to Be Considered
                    <ArrowRight size={13} />
                  </Link>
                </div>
                <p className="text-[11px] mt-4" style={{ color: 'rgba(167,163,196,0.35)' }}>
                  Partner applications are reviewed by the platform team. Not all applicants will be accepted. We maintain the quality of the perks program by being selective.
                </p>
              </div>
            </div>
          </div>

          <div
            className="mt-8 px-5 py-4 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.45)' }}>
              Perks marked as "Coming Soon" are example structures reserved for future verified partners. They do not represent real offers or endorsements. Resident benefits are real services worth knowing about, not commercial partnerships. Sponsored perks will be clearly labeled when active.
            </p>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
